import { useEffect, useRef } from 'react';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import useApiFetch from '@/hooks/useApiFetch';
import useSeoProject from '@/hooks/useSeoProject';
import { FetchApiIds } from '@/types';
import { supabase } from '@/lib/supabase';
import searchConsoleService, {
  GoogleConnectionStatus,
  SearchConsoleSummary,
  SeoOpportunities,
} from '@/services/searchConsole';
import googleAnalyticsService, { GA4DashboardSummary } from '@/services/googleAnalytics';
import pageSpeedService, { PageSpeedResult } from '@/services/pageSpeed';
import GrowthXDashboardComponent from '@/pages/GrowthXDashboard/GrowthXDashboard.component';

async function captureAndSave(
  providerToken: string,
  providerRefreshToken: string,
  refetch: () => void
) {
  try {
    const sitesRes = await fetch('https://www.googleapis.com/webmasters/v3/sites', {
      headers: { Authorization: `Bearer ${providerToken}` },
    });
    const sitesData = await sitesRes.json();
    const sites = sitesData?.siteEntry ?? [];
    await searchConsoleService.saveConnection({
      providerToken,
      providerRefreshToken,
      siteUrl: sites.length > 0 ? sites[0].siteUrl : '',
    });
    refetch();
  } catch (err) {
    console.error('[SEOx] Failed to capture Search Console connection:', err);
  }
}

const SEOxProjectOverviewContainer = function (): JSX.Element {
  const { project } = useSeoProject();
  const tokenCaptured = useRef(false);

  const activeSiteUrl = project?.siteUrl ?? null;
  const ga4PropertyId = project?.ga4PropertyId ?? null;

  // Google connection (for token availability)
  const { data: connectionStatus, isLoading: isLoadingConnection, refetch: refetchConnection } = useSeoFetch<GoogleConnectionStatus>(
    [FetchApiIds.SEO_GOOGLE_CONNECTION],
    () => searchConsoleService.getConnectionStatus(),
    { retry: false }
  );

  const isConnected = !!connectionStatus?.isConnected;
  const hasSite = isConnected && !!activeSiteUrl;

  // Search Console summary
  const { data: searchConsoleSummary, isLoading: isLoadingSummary } = useSeoFetch<SearchConsoleSummary>(
    [FetchApiIds.SEO_SEARCH_CONSOLE_SUMMARY, activeSiteUrl],
    () => searchConsoleService.getSummary(activeSiteUrl!),
    { enabled: hasSite, retry: false }
  );

  // Opportunities
  const { data: opportunities, isLoading: isLoadingOpportunities } = useSeoFetch<SeoOpportunities>(
    [FetchApiIds.SEO_OPPORTUNITIES, activeSiteUrl],
    () => searchConsoleService.getOpportunities(activeSiteUrl!),
    { enabled: hasSite, retry: false }
  );

  // GA4 summary (still returns AxiosResponse)
  const ga4Response = useApiFetch<GA4DashboardSummary, void>(
    ['seo-ga4-summary', ga4PropertyId],
    async () => googleAnalyticsService.getDashboardSummary(ga4PropertyId!),
    { enabled: isConnected && !!ga4PropertyId, retry: false }
  );
  const ga4Summary = ga4Response.data as GA4DashboardSummary | undefined;
  const isLoadingGa4 = ga4Response.isLoading;

  // PageSpeed (still returns AxiosResponse)
  const homepageUrl = activeSiteUrl?.startsWith('sc-domain:')
    ? `https://${activeSiteUrl.replace('sc-domain:', '')}`
    : activeSiteUrl;

  const pageSpeedResponse = useApiFetch<PageSpeedResult[], void>(
    ['seo-pagespeed', homepageUrl],
    async () => pageSpeedService.analyze([homepageUrl!]),
    { enabled: !!homepageUrl, retry: false }
  );
  const pageSpeedResults = pageSpeedResponse.data as PageSpeedResult[] | undefined;
  const isLoadingPageSpeed = pageSpeedResponse.isLoading;

  // Capture provider_token after Google OAuth redirect
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (tokenCaptured.current) return;
        if (!session?.provider_token) return;
        tokenCaptured.current = true;
        await captureAndSave(session.provider_token, session.provider_refresh_token ?? '', () => refetchConnection());
      }
    );
    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const run = async () => {
      if (tokenCaptured.current) return;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.provider_token) return;
      tokenCaptured.current = true;
      await captureAndSave(session.provider_token, session.provider_refresh_token ?? '', () => refetchConnection());
    };
    run();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <GrowthXDashboardComponent
      siteUrl={activeSiteUrl}
      searchConsoleSummary={searchConsoleSummary ?? null}
      opportunities={opportunities ?? null}
      ga4Summary={ga4Summary ?? null}
      pageSpeed={(pageSpeedResults ?? [])[0] ?? null}
      isLoadingSummary={isLoadingSummary && hasSite}
      isLoadingOpportunities={isLoadingOpportunities && hasSite}
      isLoadingConnection={isLoadingConnection}
      isLoadingGa4={isLoadingGa4 && !!ga4PropertyId}
      isLoadingPageSpeed={isLoadingPageSpeed && !!homepageUrl}
    />
  );
};

export default SEOxProjectOverviewContainer;
